import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  makeStyles,
  useTheme,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Grid,
  Button,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { isNil } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { VictoryChart, VictoryAxis, VictoryTheme, VictoryScatter, VictoryTooltip } from 'victory';
import moment from 'moment';

import Loading from 'common/components/loading.component';
import PageWrapper from 'modules/layout/pageWrapper.component';
import { PAGE_TITLES } from 'modules/layout/drawer/drawerItems';
import SportScalePrimaryButton from 'common/buttons/sportScalePrimaryButton.component';
import SportScaleSelectField from 'common/formFields/sportScaleSelectField.component';
import SportScaleTable from 'common/table/sportScaleTable.component';
import { getOrganizationAthletes } from 'modules/athletes/athletes.actions';
import { getWeightChangeData, getBmiData, getTargetWeightData, getWbgtData } from 'modules/reports/reports.actions';
import {
  selectIsExportLoading,
  selectWeightChangeData,
  selectBmiData,
  selectTargetWeightData,
  selectWbgtData,
} from 'modules/reports/reports.selectors';
import {
  selectSelectedOrganizationId,
  selectSelectedOrganizationName,
} from 'modules/organizations/organizations.selectors';
import { setPageTitle } from 'modules/layout/layout.actions';
import { exportToExcel } from 'utilities/reports.utils';
import ExportFieldSelectionDialog from './exportFieldSelectionDialog.component';

// Weight-type dot colors for the "Weight Over Time" scatter chart.
const WEIGHT_TYPE_COLORS = {
  IN: '#1976d2',
  OUT: '#f57c00',
  SIMPLE: '#757575',
};

const weightTypeColor = type => {
  const normalized = String(type || '').toLowerCase();
  if (normalized.includes('out')) return WEIGHT_TYPE_COLORS.OUT;
  if (normalized.includes('in')) return WEIGHT_TYPE_COLORS.IN;
  return WEIGHT_TYPE_COLORS.SIMPLE;
};

// Percent-change columns show magnitude plus a direction arrow (▲ gain / ▼ loss)
// instead of a signed number, per Sharon's request — a minus sign reads ambiguously
// next to a value that already means "loss".
const formatPctWithArrow = value => {
  if (!value) return '';
  const magnitude = Math.abs((value * 100).toFixed(2));
  return `${magnitude}${value > 0 ? '▲' : '▼'}`;
};

const RESULTS_BANNER_LINES = [
  'IN-OUT % IS LISTED BESIDE THE ANCHOR "IN"',
  'IN-IN % IS LISTED BESIDE THE MOST RECENT "IN"',
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

const ResultsBanner = () => {
  const classes = useBannerStyles();
  return (
    <Box className={classes.banner}>
      <Typography variant="subtitle2" className={classes.bannerHeading}>
        RESULTS:
      </Typography>
      {RESULTS_BANNER_LINES.map(line => (
        <Typography key={line} variant="body2" className={classes.bannerLine}>
          {line}
        </Typography>
      ))}
    </Box>
  );
};

const useBannerStyles = makeStyles(theme => ({
  banner: {
    position: 'relative',
    backgroundColor: 'rgba(188, 214, 49, 0.09)',
    borderTop: '1px solid rgba(255, 255, 255, 0.07)',
    borderRight: '1px solid rgba(255, 255, 255, 0.07)',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(1.25, 2.5),
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  bannerHeading: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.5),
  },
  bannerLine: {
    color: 'rgba(255, 255, 255, 0.82)',
    fontFamily: '"Roboto Mono", "SFMono-Regular", Menlo, Consolas, monospace',
    fontSize: '0.78rem',
    letterSpacing: '0.02em',
    lineHeight: 1.55,
  },
}));

const Reports = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();

  const [activeTab, setActiveTab] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [showFieldSelectionDialog, setShowFieldSelectionDialog] = useState(false);

  const [teamDataFilters, setTeamDataFilters] = useState({
    dateFrom: moment().subtract(30, 'days').toDate(),
    dateTo: moment().toDate(),
    selectedTeam: '',
    selectedAthlete: '',
  });

  const [athleteDataFilters, setAthleteDataFilters] = useState({
    dateFrom: moment().subtract(30, 'days').toDate(),
    dateTo: moment().toDate(),
    selectedAthlete: '',
  });

  const [targetWeightFilters, setTargetWeightFilters] = useState({
    dateFrom: moment().subtract(30, 'days').toDate(),
    dateTo: moment().toDate(),
    selectedAthlete: '',
  });

  const [wbgtFilters, setWbgtFilters] = useState({
    dateFrom: moment().subtract(30, 'days').toDate(),
    dateTo: moment().toDate(),
  });

  const organizationName = useSelector(selectSelectedOrganizationName);
  const organizationId = useSelector(selectSelectedOrganizationId);

  const isExportLoading = useSelector(selectIsExportLoading);

  // Every tab filters client-side, but the API only returns rows for the period it
  // is asked for, so request one window wide enough for all four tabs at once.
  // Keyed on the individual dates so a new object with the same dates doesn't refetch.
  const reportRange = useMemo(() => {
    const valid = dates => dates.filter(d => d && moment(d).isValid()).map(d => moment(d));
    const froms = valid([
      teamDataFilters.dateFrom,
      athleteDataFilters.dateFrom,
      targetWeightFilters.dateFrom,
      wbgtFilters.dateFrom,
    ]);
    const tos = valid([
      teamDataFilters.dateTo,
      athleteDataFilters.dateTo,
      targetWeightFilters.dateTo,
      wbgtFilters.dateTo,
    ]);
    return {
      dateFrom: froms.length ? moment.min(froms).startOf('day').toISOString() : null,
      dateTo: tos.length ? moment.max(tos).endOf('day').toISOString() : null,
    };
  }, [
    teamDataFilters.dateFrom,
    teamDataFilters.dateTo,
    athleteDataFilters.dateFrom,
    athleteDataFilters.dateTo,
    targetWeightFilters.dateFrom,
    targetWeightFilters.dateTo,
    wbgtFilters.dateFrom,
    wbgtFilters.dateTo,
  ]);

  const weightChangeData = useSelector(selectWeightChangeData);
  const bmiData = useSelector(selectBmiData);
  const targetWeightData = useSelector(selectTargetWeightData);
  const wbgtData = useSelector(selectWbgtData);

  useEffect(() => {
    if (isNil(organizationName)) return;

    dispatch(setPageTitle(`${organizationName} - ${PAGE_TITLES.ANALYSIS}`));

    if (organizationId) {
      dispatch(getBmiData(organizationId, reportRange));
      dispatch(getTargetWeightData(organizationId, reportRange));
      dispatch(getWbgtData(organizationId, reportRange));
      dispatch(getWeightChangeData(organizationId, reportRange));
    }
  }, [dispatch, organizationName, organizationId, reportRange]);

  // Team and athlete names belong to one organization, so a selection carried across
  // an org switch matches nothing: the table renders empty and the select is handed a
  // value with no matching option. Date ranges are org-agnostic and are left alone.
  useEffect(() => {
    setTeamDataFilters(prev => ({ ...prev, selectedTeam: '', selectedAthlete: '' }));
  }, [organizationId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const createDeepCopy = data => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => JSON.parse(JSON.stringify(item)));
  };

  const getFilteredBmiData = () => {
    if (!bmiData) return [];

    const filters = activeTab === 0 ? teamDataFilters : athleteDataFilters;

    const filtered = bmiData.filter(item => {
      const itemDate = moment(item.weighTime);
      if (!itemDate.isBetween(moment(filters.dateFrom), moment(filters.dateTo), 'day', '[]')) return false;

      // Only the Team Data tab exposes a team filter, so this is undefined elsewhere.
      if (filters.selectedTeam && item.teamName !== filters.selectedTeam) return false;

      if (filters.selectedAthlete && item.athleteName !== filters.selectedAthlete) return false;

      return true;
    });

    return createDeepCopy(filtered);
  };

  const getFilteredTargetWeightData = () => {
    if (!targetWeightData) return [];

    const filtered = targetWeightData.filter(item => {
      const itemDate = moment(item.weightTime);
      const dateInRange = itemDate.isBetween(
        moment(targetWeightFilters.dateFrom),
        moment(targetWeightFilters.dateTo),
        'day',
        '[]'
      );

      if (targetWeightFilters.selectedAthlete) {
        return dateInRange && item.athleteName === targetWeightFilters.selectedAthlete;
      }

      return dateInRange;
    });

    return createDeepCopy(filtered);
  };

  const getFilteredWbgtData = () => {
    if (!wbgtData) return [];

    const filtered = wbgtData.filter(item => {
      const itemDate = moment(item.wDate);
      return itemDate.isBetween(moment(wbgtFilters.dateFrom), moment(wbgtFilters.dateTo), 'day', '[]');
    });

    return createDeepCopy(filtered);
  };

  const getUniqueTeams = () => {
    if (!bmiData) return [];
    const teams = [...new Set(bmiData.map(item => item.teamName).filter(Boolean))].sort();
    return teams.map(name => ({ value: name, label: name }));
  };

  // `team` scopes the list to one team so the athlete dropdown stays consistent
  // with the team filter on the Team Data tab. Other tabs pass nothing.
  const getUniqueAthletes = (team = '') => {
    if (!bmiData) return [];
    const scoped = team ? bmiData.filter(item => item.teamName === team) : bmiData;
    const athletes = [...new Set(scoped.map(item => item.athleteName).filter(Boolean))].sort();
    return athletes.map(name => ({ value: name, label: name }));
  };

  const getChartDataWithLabels = () => {
    const filteredData = getFilteredBmiData();
    return filteredData.map(item => ({
      x: new Date(item.weighTime),
      y: item.weight,
      color: weightTypeColor(item.type),
      label: `${item.athleteName}: ${item.weight} lbs`,
    }));
  };

  const handleOpenFieldSelection = () => {
    setShowFieldSelectionDialog(true);
  };

  const handleCloseFieldSelection = () => {
    setShowFieldSelectionDialog(false);
  };

  // Always offer every section in the export dialog, even when a dataset is empty —
  // hiding empty ones made it look like the Target Weight export didn't exist.
  // exportToExcel already skips sheets that end up with no rows.
  const getAvailableDataTypes = () => ['weightChange', 'bmi', 'targetWeight', 'wbgt'];

  const getAvailableSports = () => {
    const sports = new Set();
    [weightChangeData, bmiData, targetWeightData, wbgtData].forEach(dataset => {
      if (Array.isArray(dataset)) {
        dataset.forEach(item => {
          if (item && item.teamName) sports.add(item.teamName);
        });
      }
    });
    return Array.from(sports);
  };

  const getAvailableAthletesForExport = () => {
    const athletes = new Set();
    [weightChangeData, bmiData, targetWeightData].forEach(dataset => {
      if (Array.isArray(dataset)) {
        dataset.forEach(item => {
          if (item && item.athleteName) athletes.add(item.athleteName);
        });
      }
    });
    if (Array.isArray(wbgtData)) {
      wbgtData.forEach(item => {
        if (item && item.name) athletes.add(item.name);
      });
    }
    return Array.from(athletes);
  };

  const handleExportWithSelectedFields = async (selectedFields, exportFilters, customFileName) => {
    if (isNil(organizationId)) return;

    setIsExporting(true);

    try {
      // Ensure we have fresh data. The API middleware resolves each dispatch to the
      // success action, whose `response` is the freshly-fetched array — use those
      // directly instead of the stale values captured in this render's closure
      // (which could be null/empty and silently drop a sheet, e.g. Team Data).
      // The export dialog has its own period. Fetch the union of it and the tabs'
      // window so the store stays a superset of what the tabs render afterwards;
      // exportToExcel narrows the rows to the dialog's period itself.
      const widest = (candidates, pick) => {
        const valid = candidates.filter(d => d && moment(d).isValid()).map(d => moment(d));
        return valid.length ? pick(valid).toISOString() : null;
      };
      const exportRange = {
        dateFrom: widest([exportFilters.dateFrom, reportRange.dateFrom], moment.min),
        dateTo: widest([exportFilters.dateTo, reportRange.dateTo], moment.max),
      };
      const [weightChangeResult, bmiResult, targetWeightResult, wbgtResult, athletesResult] = await Promise.all([
        dispatch(getWeightChangeData(organizationId, exportRange)),
        dispatch(getBmiData(organizationId, exportRange)),
        dispatch(getTargetWeightData(organizationId, exportRange)),
        dispatch(getWbgtData(organizationId, exportRange)),
        // The report endpoints don't return the athlete's passcode (their 4-digit
        // identifier), so pull the org roster to map athlete id -> passcode.
        dispatch(getOrganizationAthletes(organizationId)),
      ]);

      const freshOrFallback = (result, fallback) => (Array.isArray(result?.response) ? result.response : fallback);

      const passcodeByAthleteId = (Array.isArray(athletesResult?.response) ? athletesResult.response : []).reduce(
        (acc, athlete) => {
          if (athlete && athlete.id && athlete.identifier) acc[athlete.id] = athlete.identifier;
          return acc;
        },
        {}
      );

      await exportToExcel(
        {
          weightChangeData: freshOrFallback(weightChangeResult, weightChangeData),
          bmiData: freshOrFallback(bmiResult, bmiData),
          targetWeightData: freshOrFallback(targetWeightResult, targetWeightData),
          wbgtData: freshOrFallback(wbgtResult, wbgtData),
        },
        organizationName,
        selectedFields,
        exportFilters,
        customFileName,
        passcodeByAthleteId
      );

      setIsExporting(false);
    } catch (error) {
      console.error('Error exporting data:', error);
      setIsExporting(false);
    }
  };

  const buildDefaultFileName = () =>
    organizationName ? `${organizationName}_Athletes_Weight_Report_${moment().format('YYYY-MM-DD')}` : '';

  if (isNil(organizationName)) {
    return (
      <PageWrapper>
        <Loading />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Box className={classes.container} style={{ width: '-webkit-fill-available' }}>
        <Box className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            Reports
          </Typography>

          <SportScalePrimaryButton
            onClick={handleOpenFieldSelection}
            disabled={isExporting || isExportLoading}
            className={classes.exportButton}
          >
            {isExporting || isExportLoading ? (
              <>
                <CircularProgress size={20} className={classes.spinner} />
                Exporting...
              </>
            ) : (
              'Export Excel Spreadsheet'
            )}
          </SportScalePrimaryButton>
        </Box>

        <Paper className={classes.paper}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fixed"
          >
            <Tab label="Team Data" />
            <Tab label="Athlete Data" />
            <Tab label="Target Weight Data" />
            <Tab label="WBGT Data" />
          </Tabs>

          <TabPanel value={activeTab} index={0} width="100%">
            <Grid container spacing={3} className={classes.filtersContainer}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Date From"
                  value={teamDataFilters.dateFrom}
                  onChange={date => setTeamDataFilters(prev => ({ ...prev, dateFrom: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Date To"
                  value={teamDataFilters.dateTo}
                  onChange={date => setTeamDataFilters(prev => ({ ...prev, dateTo: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SportScaleSelectField
                  label="Select Team"
                  value={teamDataFilters.selectedTeam}
                  onChange={e => {
                    const selectedTeam = e.target.value;
                    setTeamDataFilters(prev => ({
                      ...prev,
                      selectedTeam,
                      // Keep the athlete only while they're still in scope. Narrowing to a
                      // different team would otherwise leave a name that filters the table to
                      // nothing; widening back to All Teams keeps the selection intact.
                      selectedAthlete: getUniqueAthletes(selectedTeam).some(
                        option => option.value === prev.selectedAthlete
                      )
                        ? prev.selectedAthlete
                        : '',
                    }));
                  }}
                  options={[{ value: '', label: 'All Teams' }, ...getUniqueTeams()]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SportScaleSelectField
                  label="Select Athlete"
                  value={teamDataFilters.selectedAthlete}
                  onChange={e => setTeamDataFilters(prev => ({ ...prev, selectedAthlete: e.target.value }))}
                  options={[{ value: '', label: 'All Athletes' }, ...getUniqueAthletes(teamDataFilters.selectedTeam)]}
                  fullWidth
                />
              </Grid>
            </Grid>

            <ResultsBanner />
            <SportScaleTable
              title="Team Data"
              columns={[
                { title: 'Athlete Name', field: 'athleteName' },
                { title: 'Team Name', field: 'teamName' },
                { title: 'Date', field: 'weighTime', type: 'date' },
                {
                  title: 'Time of Weight',
                  field: 'weighTime',
                  render: rowData => (rowData.weighTime ? moment(rowData.weighTime).format('h:mm A') : ''),
                },
                { title: 'Weight', field: 'weight', type: 'numeric' },
                {
                  title: 'In2In%',
                  field: 'in2In',
                  type: 'numeric',
                  render: rowData => formatPctWithArrow(rowData.in2In),
                },
                {
                  title: 'In2Out%',
                  field: 'in2OutPct',
                  type: 'numeric',
                  render: rowData => formatPctWithArrow(rowData.in2OutPct),
                },
                { title: 'DOB', field: 'dob', type: 'date' },
                { title: 'Gender', field: 'gender' },
                {
                  title: 'Height (in.)',
                  field: 'height',
                  type: 'numeric',
                  render: rowData => (rowData.height ? (rowData.height / 2.54).toFixed(2) : ''),
                },
                { title: 'Grad Year', field: 'graduationYear', type: 'numeric' },
                { title: 'Note', field: 'athleteNote' },
              ]}
              data={getFilteredBmiData()}
              isLoading={isNil(bmiData)}
              options={{
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                exportButton: false,
                filtering: false,
                sorting: true,
                maxBodyHeight: 440,
                emptyRowsWhenPaging: false,
                headerStyle: {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1} width="100%">
            <Grid container spacing={3} className={classes.filtersContainer}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Date From"
                  value={athleteDataFilters.dateFrom}
                  onChange={date => setAthleteDataFilters(prev => ({ ...prev, dateFrom: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Date To"
                  value={athleteDataFilters.dateTo}
                  onChange={date => setAthleteDataFilters(prev => ({ ...prev, dateTo: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SportScaleSelectField
                  label="Select Athlete"
                  value={athleteDataFilters.selectedAthlete}
                  onChange={e => setAthleteDataFilters(prev => ({ ...prev, selectedAthlete: e.target.value }))}
                  options={[{ value: '', label: 'All Athletes' }, ...getUniqueAthletes()]}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box className={classes.chartContainer}>
              <Typography variant="h6" gutterBottom>
                Weight Over Time
              </Typography>
              <Box className={classes.chartLegend}>
                {Object.entries(WEIGHT_TYPE_COLORS).map(([label, color]) => (
                  <Box key={label} className={classes.chartLegendItem}>
                    <span className={classes.chartLegendSwatch} style={{ backgroundColor: color }} />
                    <Typography variant="caption">{label}</Typography>
                  </Box>
                ))}
              </Box>
              <VictoryChart
                theme={VictoryTheme.material}
                height={300}
                width={800}
                scale={{ x: 'time' }}
                padding={{ left: 80, top: 40, right: 80, bottom: 60 }}
                style={{
                  zIndex: 1,
                }}
              >
                <VictoryAxis
                  dependentAxis
                  style={{
                    tickLabels: { fill: theme.palette.common.white },
                    axis: { stroke: theme.palette.common.white },
                    grid: { stroke: theme.palette.common.white },
                    data: { fill: theme.palette.common.white },
                  }}
                />
                <VictoryAxis
                  style={{
                    tickLabels: { fill: theme.palette.common.white },
                    axis: { stroke: theme.palette.common.white },
                    grid: { stroke: theme.palette.common.white },
                    data: { fill: theme.palette.common.white },
                  }}
                />
                <VictoryScatter
                  data={getChartDataWithLabels()}
                  x="x"
                  y="y"
                  size={4}
                  labels={({ datum }) => datum.label}
                  labelComponent={
                    <VictoryTooltip
                      flyoutStyle={{ fill: 'white', stroke: 'gray' }}
                      style={{ fill: 'black' }}
                      renderInPortal={false}
                    />
                  }
                  style={{
                    data: { fill: ({ datum }) => datum.color },
                  }}
                />
              </VictoryChart>
            </Box>

            <ResultsBanner />
            <SportScaleTable
              title="Athlete Data"
              columns={[
                { title: 'Date', field: 'weighTime', type: 'date' },
                {
                  title: 'Time of Weight',
                  field: 'weighTime',
                  render: rowData => (rowData.weighTime ? moment(rowData.weighTime).format('h:mm A') : ''),
                },
                { title: 'Weight', field: 'weight', type: 'numeric' },
                {
                  title: 'In2In%',
                  field: 'in2In',
                  type: 'numeric',
                  render: rowData => formatPctWithArrow(rowData.in2In),
                },
                {
                  title: 'In2Out%',
                  field: 'in2OutPct',
                  type: 'numeric',
                  render: rowData => formatPctWithArrow(rowData.in2OutPct),
                },
                {
                  title: 'Weight Type',
                  field: 'type',
                  type: 'string',
                  render: rowData => (rowData.type ? rowData.type.charAt(0).toUpperCase() + rowData.type.slice(1) : ''),
                },
                { title: 'DOB', field: 'dob', type: 'date' },
                { title: 'Gender', field: 'gender' },
                {
                  title: 'Height (in.)',
                  field: 'height',
                  type: 'numeric',
                  render: rowData => (rowData.height ? (rowData.height / 2.54).toFixed(2) : ''),
                },
                { title: 'Grad Year', field: 'graduationYear', type: 'numeric' },
                { title: 'Note', field: 'athleteNote' },
              ]}
              data={getFilteredBmiData()}
              isLoading={isNil(bmiData)}
              options={{
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                exportButton: false,
                filtering: false,
                sorting: true,
                maxBodyHeight: 440,
                emptyRowsWhenPaging: false,
                headerStyle: {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3} className={classes.filtersContainer}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Date From"
                  value={targetWeightFilters.dateFrom}
                  onChange={date => setTargetWeightFilters(prev => ({ ...prev, dateFrom: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Date To"
                  value={targetWeightFilters.dateTo}
                  onChange={date => setTargetWeightFilters(prev => ({ ...prev, dateTo: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SportScaleSelectField
                  label="Select Athlete"
                  value={targetWeightFilters.selectedAthlete}
                  onChange={e => setTargetWeightFilters(prev => ({ ...prev, selectedAthlete: e.target.value }))}
                  options={[{ value: '', label: 'All Athletes' }, ...getUniqueAthletes()]}
                  fullWidth
                />
              </Grid>
            </Grid>

            <SportScaleTable
              title="Target Weight Data"
              columns={[
                { title: 'Athlete Name', field: 'athleteName' },
                { title: 'Target Weight', field: 'targetWeightT', type: 'numeric' },
                { title: 'Target Date', field: 'targetDate', type: 'date' },
                { title: 'Weight', field: 'weight', type: 'numeric' },
                { title: 'Weight Date', field: 'weightTime', type: 'date' },
                {
                  title: 'Lbs To Goal',
                  field: 'lbsToGoal',
                  type: 'numeric',
                  render: rowData => (rowData.targetWeightT ? (rowData.targetWeightT - rowData.weight).toFixed(2) : ''),
                },
                {
                  title: 'Days To Goal',
                  field: 'daysToGoal',
                  type: 'numeric',
                  render: rowData => (rowData.targetDate ? moment(rowData.targetDate).diff(moment(), 'days') : ''),
                },
              ]}
              data={getFilteredTargetWeightData()}
              isLoading={isNil(targetWeightData)}
              options={{
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                exportButton: false,
                filtering: false,
                sorting: true,
              }}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3} className={classes.filtersContainer}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Date From"
                  value={wbgtFilters.dateFrom}
                  onChange={date => setWbgtFilters(prev => ({ ...prev, dateFrom: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Date To"
                  value={wbgtFilters.dateTo}
                  onChange={date => setWbgtFilters(prev => ({ ...prev, dateTo: date }))}
                  format="MM/DD/YYYY"
                  fullWidth
                />
              </Grid>
            </Grid>

            <SportScaleTable
              title="WBGT Data"
              columns={[
                { title: 'Team Name', field: 'teamName' },
                { title: 'Athlete Name', field: 'name' },
                { title: 'Date', field: 'wDate', type: 'date' },
                { title: 'Initial Value', field: 'initial', type: 'numeric' },
                { title: 'Peak Value', field: 'peak', type: 'numeric' },
              ]}
              data={getFilteredWbgtData()}
              isLoading={isNil(wbgtData)}
              options={{
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                exportButton: false,
                filtering: false,
                sorting: true,
              }}
            />
          </TabPanel>
        </Paper>

        <ExportFieldSelectionDialog
          open={showFieldSelectionDialog}
          onClose={handleCloseFieldSelection}
          onExport={handleExportWithSelectedFields}
          availableDataTypes={getAvailableDataTypes()}
          availableSports={getAvailableSports()}
          availableAthletes={getAvailableAthletesForExport()}
          defaultFileName={buildDefaultFileName()}
        />
      </Box>
    </PageWrapper>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  chartContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    textAlign: 'center',
    marginTop: theme.spacing(2),
    zIndex: 1,
  },
  chartLegend: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  chartLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  chartLegendSwatch: {
    display: 'inline-block',
    width: 14,
    height: 14,
    borderRadius: '50%',
  },
  exportButton: {
    marginLeft: theme.spacing(2),
  },
  spinner: {
    marginRight: theme.spacing(1),
  },
}));

export default Reports;
